<template>
  <div class="page-container" v-if="currentNews && currentNews.title">
    <h1 class="page-title">{{ currentNews.title }}</h1>
    <p class="page-date" v-if="currentNews.date">{{ currentNews.date }}</p>
    <div class="content" :class="langStore.currentLang.startsWith('zh') ? 'lang-zh' : 'lang-en'">
      <!-- 第一段前：图1、图2 -->
      <div v-if="getPicture(1) || getPicture(2)" class="content-section image-section image-grid grid-2">
        <div v-for="n in [1, 2]" :key="n" v-show="getPicture(n)" class="image-container">
          <img :src="getPicture(n)?.url" :alt="getPicture(n)?.caption || 'News Image'">
          <p v-if="getPicture(n)?.caption" class="image-caption">{{ getPicture(n)?.caption }}</p>
        </div>
      </div>

      <template v-for="(section, index) in contentSections" :key="index">
        <div class="content-section">
          <div class="text-block">
            <p v-html="section"></p>
          </div>
        </div>

        <!-- 按段落插入配图 -->
        <template v-if="pictureRows[index]">
          <div
            v-for="(row, rowIndex) in pictureRows[index]"
            :key="rowIndex"
            class="content-section image-section image-grid"
            :class="[`grid-${row.length}`, row.length === 1 ? 'grid-center' : '', row.length === 1 && row[0] === 8 ? 'grid-narrow' : '']"
          >
            <div v-for="n in row" :key="n" v-show="getPicture(n)" class="image-container">
              <img :src="getPicture(n)?.url" :alt="getPicture(n)?.caption || 'News Image'">
              <p v-if="getPicture(n)?.caption" class="image-caption">{{ getPicture(n)?.caption }}</p>
            </div>
            <p v-if="index === 1 && rowIndex === 0" class="image-caption row-caption">{{ toppingOutCaption }}</p>
          </div>
        </template>
      </template>
    </div>
  </div>
  <div v-else class="page-container">
    <p>新闻内容未找到</p>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useLangStore } from '../../../pinia/stores/lang';
import zhData from '../../../i18n/zh';
import enData from '../../../i18n/en';

const langStore = useLangStore();

const currentNews = computed(() => {
  const translationData = langStore.currentLang.startsWith('zh') ? zhData : enData;
  const newsList = translationData.pages.news.company.news.list;
  const newsItem = newsList.find((news: any) => news.id === 'sst-phase3-topping-out');
  return newsItem || {};
});

const contentSections = computed(() => {
  const content = (currentNews.value as any)?.content || '';
  return content ? content.split('---').filter((p: string) => p.trim()) : [];
});

const getPicture = (n: number) => (currentNews.value as any)?.other?.[`picture${n}`] || null;

// 各段落后需要展示的图片编号（外层为行，数组内为并排图片）
const pictureRows: Record<number, number[][]> = {
  0: [[3]],
  1: [[4, 5]],
  2: [[6]],
  3: [[7, 9], [8]],
};

const toppingOutCaption = computed(() =>
  langStore.currentLang.startsWith('zh')
    ? '上海超导总部基地项目封顶活动'
    : "Topping-out ceremony of Shanghai Superconductor's headquarters base project"
);
</script>

<style scoped>
.page-container {
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
  font-family: 'Microsoft YaHei', Arial, 'PingFang SC', sans-serif;
}

.page-title {
  font-size: 28px;
  font-weight: bold;
  color: #333;
  margin-bottom: 10px;
  line-height: 1.4;
  text-align: center;
}

.page-date {
  color: #666;
  font-size: 14px;
  margin-bottom: 30px;
  text-align: right;
}

.content {
  line-height: 1.8;
  color: #444;
}

.content-section {
  margin-bottom: 20px;
}

.content.lang-zh .text-block :deep(p) {
  margin-bottom: 15px;
  text-align: justify;
  font-size: 16px;
  text-indent: 2em;
}

.content.lang-en .text-block :deep(p) {
  margin-bottom: 15px;
  text-align: justify;
  font-size: 16px;
  text-indent: 0;
}

.text-block :deep(a) {
  color: #1677ff;
  text-decoration: underline;
  cursor: pointer;
}

.text-block :deep(a:hover) {
  color: #0958d9;
}

.image-section {
  margin-top: 20px;
  margin-bottom: 20px;
}

.image-grid {
  display: grid;
  gap: 15px;
}

.image-grid.grid-1 {
  grid-template-columns: 1fr;
}

.image-grid.grid-2 {
  grid-template-columns: repeat(2, 1fr);
}

.image-grid.grid-3 {
  grid-template-columns: repeat(3, 1fr);
}

.grid-center .image-container {
  max-width: 100%;
  margin: 0 auto;
}

/* 竖版图片单独居中展示，避免被横向拉伸 */
.grid-narrow .image-container {
  max-width: 420px;
}

.image-container {
  text-align: center;
}

.image-container img {
  width: 100%;
  height: auto;
  object-fit: contain;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.image-caption {
  margin-top: 8px;
  color: #666;
  font-size: 14px;
  font-style: italic;
}

.row-caption {
  grid-column: 1 / -1;
  margin-top: 0;
  text-align: center;
}

@media (max-width: 768px) {
  .image-grid.grid-2,
  .image-grid.grid-3 {
    grid-template-columns: 1fr;
  }
}
</style>
